import { useNode } from "@craftjs/core";
import getConfig from "next/config";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ContentLibraryDialog } from "@/components/shared/content-library/content-library-dialog";
import { ContentLibrary } from "@/components/shared/content-library/content-library-loader";
import { useContentLibraryTranslations } from "@/components/shared/content-library/use-content-library-translations";
import { useContentPageRouteValues } from "@/content-hub/editor/use-content-page-route-values";
import { useSpaceFilter } from "@/content-hub/editor/use-space-filter";
import { logProductAnalyticsEvent } from "@/features/analytics";
import { useAppDispatch } from "@/redux/store";

import type { VideoGridProps } from "../video-grid";

const { publicRuntimeConfig } = getConfig();

export const LibrarySetting = () => {
  const { t, i18n } = useTranslation();
  const { organizationExternalId } = useContentPageRouteValues();
  const spaceExternalId = useSpaceFilter();
  const dispatch = useAppDispatch();
  const translations = useContentLibraryTranslations();

  const {
    props,
    actions: { setProp },
  } = useNode((query) => {
    const filteredVideoList = query.props.videoList.filter((video) => {
      return typeof video !== "string";
    });
    return {
      props: {
        ...(query.props as VideoGridProps),
        videoList: filteredVideoList,
      },
    };
  });

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const selectedRecordingIds = props.videoList
    .map((video) => {
      return video.recordingId;
    })
    .filter(Boolean);

  return (
    <div className="mt-4 flex flex-col gap-y-4">
      <button
        className="h-10 rounded-md bg-primary px-4 py-2 text-sm text-white"
        onClick={() => setIsLibraryOpen(true)}
      >
        {t("preference-panel.video-grid-choose-videos")}
      </button>
      <ContentLibraryDialog
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        unmount={true}
      >
        <ContentLibrary
          options={{
            host: publicRuntimeConfig.HOPIN_ROOT,
            organisationId: organizationExternalId,
            spaceExternalId,
            i18n: {
              locale: i18n.language,
              translations,
            },
          }}
          onClose={() => setIsLibraryOpen(false)}
          allowSingleSelection={false}
          selectedRecordingIds={selectedRecordingIds}
          onConfirmSelection={(recordingIds) => {
            dispatch({
              type: "[page editor] fetch recordings",
              payload: {
                organizationExternalId,
                recordingIds,
              },
            });
            setProp((props: VideoGridProps) => {
              const embededVideos = props.videoList.reduce(
                (videos, currentVideo) => {
                  if (
                    typeof currentVideo === "string" ||
                    currentVideo.kind === "recording"
                  ) {
                    return videos;
                  }
                  return [...videos, currentVideo];
                },
                []
              );
              const recordings = recordingIds.map((recordingId) => ({
                kind: "recording",
                recordingId,
              }));
              props.videoList = [...recordings, ...embededVideos];
            });
            logProductAnalyticsEvent({
              name: "ch_canvas_block_edit_video_lib",
              properties: { recordingIds },
            });
          }}
        />
      </ContentLibraryDialog>
    </div>
  );
};
